import type { trackGameClick } from '@comeon/analytics';
import gridTemplates from './explore/grids';
import { EnhancedAndFormattedImageView } from './utilities/enhanceImage';

export interface GameImage {
	path: string;
	file: string;
}

export interface GameProvider {
	name: string;
	code: string;
}

export interface GameTag {
	name: string;
	priority?: number;
	type?: string;
	text: string;
}

export interface Game {
	gameSiteId: number;
	gameId: number;
	code: string;
	publishCode?: string;
	liveFeedCode?: string;
	categoryCode: string;
	provider: GameProvider;
	name: string;
	categoryLabel: string;
	visible: boolean;
	imageView: GameImage;
	otherView?: GameImage;
	device: string;
	native: boolean;
	jackpot?: string;
	index?: number;
	isNew?: boolean;
	isHot?: boolean;
	casinoSubProvider?: string;
	casinoSubProviderVO?: GameProvider;
	tags?: GameTag[];
	hasFunMode?: boolean;
	jackpotName?: string;
	inhouseJackpots?: { id: string }[];
}

export type GameLoaderData = {
	productCode: string;
	gameLoaderResponse:
		| {
				gameLoadType: 'REDIRECTION' | 'SCRIPT' | 'IFRAME';
				errorMessage: string;
				data: {
					URL: string;
				};
				provider: string;
				regulation: string;
				gameSessionId?: string;
		  }
		| {
				gameLoadType: 'JSON';
				errorMessage: string;
				data: {
					JSON: string;
					URL: string;
				};
				provider: string;
				regulation: string;
				gameSessionId?: string;
		  };
};

export type GameLoaderResponse =
	| {
			status: 'FAILURE';
			errorCode: string;
			errorDescription: string;
	  }
	| {
			status: 'SUCCESS';
			result: GameLoaderData;
	  };

export interface EnhancedGame extends Omit<Game, 'imageView'> {
	realModeUrl: string;
	funModeUrl: string;
	imageView: EnhancedAndFormattedImageView;
	gameLoaderResponse?: GameLoaderData['gameLoaderResponse'] | null;
	gameRequestPending?: boolean;
	status?: string;
	hydrated?: boolean;
	loadAs?: string;
	gameMode?: string;
	errorCode?: string;
	errorDescription?: string;
}

export type CampaignImage = {
	path: string;
	file: string;
	src: string;
	srcSmall: string;
	srcSet: string;
};

type MobileLayout = 'HORIZONTAL' | 'VERTICAL' | 'SHORT';
type DesktopLayout = 'PACKED' | 'SPACED';
type TextPosition = 'LEFT' | 'CENTER' | 'RIGHT';
type TextAlignment = 'LEFT' | 'CENTER';
type ImagePosition = 'LEFT' | 'RIGHT';

export interface CampaignOld {
	backgroundImageURL: string;
	foregroundImageURL: string;
	externalWindow: boolean;
	buttonText: string;
	messageText: string;
	link: string;
	id: number;
	headerText?: string;
	backgroundImage: CampaignImage;
	foregroundImage: CampaignImage;
}

export interface Campaign {
	id: number;
	name: string;
	headerText?: string;
	descriptionText?: string;
	textPosition: TextPosition;
	textAlign: TextAlignment;
	backgroundImage: CampaignImage;
	url?: string;
	ctaText?: string;
	tcText?: string;
	tcImage?: CampaignImage;
	foregroundImage1?: CampaignImage;
	foregroundImage1Position?: ImagePosition;
	foregroundImage2?: CampaignImage;
	mobileLayoutType: MobileLayout;
	desktopLayoutType: DesktopLayout;
	openNewWindow: boolean;
}

export type EnhancedCampaignImage = {
	src: string;
	srcSmall: string;
	srcSet: string;
	file: string;
	path: string;
};

export type EnhancedCampaignOld = Omit<CampaignOld, 'backgroundImage' | 'foregroundImage'> & {
	backgroundImage: EnhancedCampaignImage;
	foregroundImage: EnhancedCampaignImage;
};

export type EnhancedCampaign = Omit<
	Campaign,
	'backgroundImage' | 'foregroundImage1' | 'foregroundImage2' | 'tcImage'
> & {
	backgroundImage: EnhancedCampaignImage;
	foregroundImage1?: EnhancedCampaignImage;
	foregroundImage2?: EnhancedCampaignImage;
	tcImage?: EnhancedCampaignImage;
};

type BaseCategory = {
	id: string;
	order: number;
	itemDisplayType: 'xs' | 'm' | 's' | 'l';
	gameIds: number[];
	gameCodes: string[];
	gamesByIdx?: number[];
	staticImageEnabled?: boolean;
	title?: string;
};

export type WidgetCategory = BaseCategory & {
	gameLaunch: string;
	otherExclusivesUrl: string;
	labelText: string;
	comingSoonText: string;
	availableNowText: string;
	otherExclusivesText: string;
	backgroundImage: string;
	foregroundImage: string;
	previewImage: string;
	trailerUrl: string;
	widgetId: string;
	gameIds: number[];
	exploreGameItems: [
		{
			code: string;
			id: number;
			name: string;
			mobile: boolean;
		},
		{
			code: string;
			id: number;
			name: string;
			mobile: boolean;
		}
	];
	type: 'exclusive_game_widget';
};

export type RecommendedCategory = BaseCategory & {
	gridType: keyof typeof gridTemplates;
	title: string;
	type: 'recommended';
};

export interface Tournament {
	id: number;
	name: string;
	image: GameImage;
	otherView?: GameImage;
	userJoined: boolean;
	hasStarted: boolean;
	startDate: string;
	endDate: string;
	description: string;
	pointsRule: string;
	tags: string[];
	currencyCode: string;
	minBet: number;
	totalPrize?: number;
	prizePoolText?: string;
	playerCount: number;
}

export type TournamentsCategory = BaseCategory & {
	type: 'tournaments';
	tournaments: Tournament[];
};

export type RegularWinType = {
	winAmount: number;
	playerName: string;
	gameIdx: number;
	gameCode: string;
	winType: 'GAME_DEPOSIT_CASINO' | 'INHOUSE_JACKPOT_DEPOSIT' | 'GAME_JACKPOT_DEPOSIT' | 'GAME_DEPOSIT_CASH_REWARD';
};

export type TournamentWinType = {
	winAmount: number;
	playerName: string;
	tournamentId: string;
	tournamentImage: string;
	tournamentName: string;
	winType: 'TOURNAMENT_AUTO_PAYOUT' | 'TOURNAMENT_MANUAL_PAYOUT';
};

export type Winner = TournamentWinType | RegularWinType;

export function assertIsTournamentWinType(winner: Winner): winner is TournamentWinType {
	return (winner as TournamentWinType).tournamentId !== undefined;
}

export type WinnerFeedCategory = BaseCategory & {
	winners: Winner[];
	type: 'winner_feed';
};
export type ContinuePlayingCategory = BaseCategory & {
	type: 'continue_playing';
};
export type SimpleCategory = BaseCategory & {
	gridType: keyof typeof gridTemplates;
	title: string;
	desc: string;
	type: 'simple';
};

export type Category =
	| WidgetCategory
	| RecommendedCategory
	| TournamentsCategory
	| WinnerFeedCategory
	| ContinuePlayingCategory
	| SimpleCategory;

export type EnhancedCategory =
	| WidgetCategory
	| RecommendedCategory
	| TournamentsCategory
	| (Omit<WinnerFeedCategory, 'winners'> & { winners: Winner[] })
	| ContinuePlayingCategory
	| SimpleCategory;

export type LFSJackpotResponse = { gameNames: string[]; amount: number; productCode: string }[];

export type LovedAndRecent = { loved: string[]; recent: string[] };

export type SocketJackpot = {
	pools: SocketJackpot[];
	amount: number;
	id: number;
	name: string;
	targetTime: string;
	currency: string;
};

export type Filters = {
	provider: string[];
	feature: string[];
	amount: string[];
};

export type TrackGameClickParamsFromCategory = Omit<
	Parameters<typeof trackGameClick>[0],
	'listName' | 'isLiveCasinoLocation' | 'category'
>;

export interface InhouseJackpotTier {
	id: string;
	value: number;
	name: string;
	isPremium?: boolean;
	premiumJackpotId?: string;
	description?: string;
	formattedTierValue?: string;
}
